import { UntilDestroy } from '@ngneat/until-destroy';
import { computed, inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Route } from 'dataModels/route';
import { selectPreviousPageIndex } from 'shared/base/generics/store/quote-controller/quote-controller.selectors';
import {
    selectQuoteFlowType,
    showEmbeddedCashback as showEmbeddedCashbackSelector,
} from 'stores/quote/quote.selectors';
import { QuoteFlowType } from 'sharedEnums/quote-flow-type';
import { AppConfigService } from 'shared/base/generics/services/app-config.service';
import { BrandNames } from 'shared/base/generics/enums/brand-names';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class EmbeddedCashbackNavigationService {
    private readonly appConfigService = inject(AppConfigService);

    /**
     * Constructor for the EmbeddedCashbackNavigationService.
     * @param store$ - The NgRx store used to select state signals.
     */
    public constructor(private readonly store$: Store) {}

    /**
     * Signals for retrieving navigation data from the store.
     */
    private readonly previousPageIndex = this.store$.selectSignal(selectPreviousPageIndex());
    private readonly quoteFlowType = this.store$.selectSignal(selectQuoteFlowType);
    private readonly showEmbeddedCashbackFromStore = this.store$.selectSignal(
        showEmbeddedCashbackSelector,
    );

    private readonly showEmbeddedCashback = computed(
        () =>
            this.showEmbeddedCashbackFromStore() ||
            this.appConfigService.config().brand === BrandNames.VIRSEKER,
    );

    /**
     * Resolves the conditional page activation for the policyholder based on the page name and route.
     * @param pageName - The name of the page to resolve the route for.
     * @param route - The default route to be used.
     * @returns The resolved route based on the page name.
     */
    public resolveConditionalPageActivation(route: Route): Route {
        // Check if this is a back navigation request or if the embedded cashback should not be shown, and return the default route if so.
        if (this.isBackNavigation(route) || !this.showEmbeddedCashback()) {
            return route;
        }

        const isVehicleFlow = this.quoteFlowType() === QuoteFlowType.Vehicle;
        if (isVehicleFlow) {
            return {
                baseRoute: 'car-insurance',
                pageRoute: 'premium',
                sectionRoute: 'quote',
            };
        } else {
            return {
                baseRoute: 'home-contents',
                pageRoute: 'premium',
                sectionRoute: 'quote',
            };
        }
    }

    /**
     * Determines if the current navigation request is a back navigation.
     * @param route - The route being processed.
     * @returns True if this appears to be back navigation.
     */
    private isBackNavigation(route: Route): boolean {
        const previousPage = this.previousPageIndex();
        return previousPage?.route?.pageRoute === route?.pageRoute;
    }
}
